import { useEffect } from 'react';
import { AppBar, Toolbar, useTheme, useMediaQuery } from '@mui/material';
import { ReactComponent as HamburgerIcon } from '../../icons/HamburgerIcon.svg';
import { ReactComponent as HamburgerCloseIcon } from '../../icons/HamburgerCloseIcon.svg';
import logo from '../../loginImages/spinnaker-black.svg';

import { myPrimaryColor } from "../../constants/colors";
import { myVJBlue200 } from "../../constants/brandingColors";

// context
import {
  useAppSiteState,
  useAppConfigState,
  useAppUserState,
  useVrsTranslationState,
  useAppGlobalState,
  useAppCompanyState,
} from '../../context/AppContext/AppContext';
import { SimpleLinearProgress } from '../../components/SimpleLinearProgress/SimpleLinearProgress';
import { OUTAGE_CHECK_MILLISECONDS } from '../../constants/global';
import { useVrsOutageActions } from '../../actions/vrsOutageActions';
import { useBannerActions } from '../../hooks/useBanner';
import { createOutageBanner } from '../../components/Banner/OutageBannerUtils';
import BannerContainer from '../../components/Banner/BannerContainer';

import { styled, IconButton } from "@mui/material";
import { useMiniSidebar } from '../../context/MiniSidebarContext';
import { myVJWhite, myVJBlack950, myVJBlack200 } from '../../constants/brandingColors';
import { useNavigate } from 'react-router'; // Add for navigation
import CompanySiteHeader from './CompanySiteHeader';
import HeaderTitle from './HeaderTitle/HeaderTitle';

const HeaderTopContainer = styled("div")({
  position: 'relative',
});

const AppBarContainer = styled(AppBar)(({ theme }) => ({
  '&.MuiAppBar-root': { // Increase specificity
    zIndex: theme.zIndex.drawer + 1,
    backgroundColor: myVJWhite,
    height: 'auto',
    padding: '0px !important',
    margin: '0px !important',
    boxShadow: 'none', // Remove any conflicting shadows
    [theme.breakpoints.up('lg')]: {
      left: '64px !important',
      width: 'calc(100% - 64px) !important',
    },
    [theme.breakpoints.down('md')]: {
      left: '0px !important',
      width: '100% !important',
    },
  }
}));

// Update the StyledToolbar to accept headerTitleInfo as a prop
const StyledToolbar = styled(Toolbar)<{ hasHeaderTitleInfo?: boolean }>(({ theme, hasHeaderTitleInfo }) => ({
  paddingTop: '16px !important',
  paddingBottom: '16px !important',
  paddingLeft: '16px !important',
  paddingRight: '16px !important',
  flexDirection: 'column',
  alignItems: 'stretch',
  minHeight: 'auto',
  height: 'auto',
  [theme.breakpoints.up('md')]: {
    paddingTop: '20px !important',
    paddingBottom: '20px !important',
    paddingLeft: '40px !important',
    paddingRight: '40px !important',
  },
  [theme.breakpoints.down('md')]: {
    minHeight: hasHeaderTitleInfo ? '150px' : '126px', // Different minHeight based on headerTitleInfo
  },
  borderBottom: `1px solid ${myVJBlack200}`,
}));



/* Logo Section - New component for logo and platform name on mobile */
const LogoSection = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  cursor: "pointer",
}));

const LogoImage = styled("img")(({ theme }) => ({
  height: '32px',
  width: 'auto',
  marginRight: '12px',
  [theme.breakpoints.up("lg")]: {
    display: "none", // Hide on desktop
  }
}));

// Line 1: Logo + Platform name + Hamburger (mobile) or Platform name + Breadcrumb + Company/Site (desktop)
const Line1Container = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
  minHeight: "24px",
  [theme.breakpoints.down("md")]: {
    minHeight: "40px",
  },
  marginBottom: 0, // Remove margin, use gap from parent
}));

// Add a container for logo + platform name on mobile
const LeftSideContainer = styled("div")({
  display: "flex",
  alignItems: "center",
});

// Line 2: Company name and site name (mobile/tablet only)
const Line2Container = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-start",
  alignItems: "center",
  marginTop: '18px', // 16px + 2px for gap
  width: "100%",
  minHeight: "18px",
  marginBottom: 0, // Remove margin, use gap from parent
  [theme.breakpoints.up("lg")]: {
    display: "none", // Hide on desktop
  }
}));

// Line 3: Breadcrumb (mobile/tablet only)
const Line3Container = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-start",
  alignItems: "center",
  width: "100%",
  minHeight: "18px",
  marginTop: '16px',
  marginBottom: 0,
  flexGrow: 0,   // Prevent the container from growing beyond content
  minWidth: 0,   // Allow content to determine minimum width
  [theme.breakpoints.up("lg")]: {
    display: "none",
  }
}));

// Update PlatformName to match your HeadeTitle.tsx styling
const PlatformName = styled("div")({
  fontFamily: 'Poppins, sans-serif',
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: 600,
  lineHeight: '24px',
  color: myPrimaryColor,
  flexShrink: 0,
});


// Create a styled container for breadcrumbs with proper spacing
const BreadcrumbContainer = styled("div")(({ theme }) => ({
  minWidth: 0,   // Allow content to determine width
  width: 'fit-content', // Container only as wide as its content
  '& .MuiBreadcrumbs-root': {
    padding: '0px',
    margin: '0px',
    fontSize: '12px',
  },
  '& .MuiBreadcrumbs-ol': {
    padding: '0px',
    margin: '0px',
    gap: theme.spacing(1), // 8px gap between breadcrumb items
  },
  '& .MuiBreadcrumbs-li': {
    padding: '0px',
    margin: '0px',
    fontFamily: 'Poppins, sans-serif',
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: '18px',
  },
  '& .MuiTypography-root': {
    fontFamily: 'Poppins, sans-serif',
    fontSize: '12px !important',
    fontWeight: 400,
    lineHeight: '18px',
    color: myPrimaryColor,
    margin: '0px',
    padding: '0px',
  },
  '& .MuiLink-root': {
    fontFamily: 'Poppins, sans-serif',
    fontSize: '12px !important',
    fontWeight: 400,
    lineHeight: '18px',
    color: myPrimaryColor,
    textDecoration: 'none',
    margin: '0px',
    padding: '0px',
    '&:hover': {
      textDecoration: 'underline',
    }
  },
  '& .MuiBreadcrumbs-separator': {
    margin: '0 4px',
    color: myPrimaryColor,
  },
  '& .MuiSvgIcon-root': {
    fontSize: '12px',
    color: myPrimaryColor,
  }
}));

const HamburgerButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'isOpen',
})<{ isOpen?: boolean }>(({ isOpen, theme }) => ({
  backgroundColor: isOpen ? myVJBlue200 : myVJWhite,
  color: isOpen ? myVJWhite : myVJBlack950,
  borderRadius: 5,
  padding: '8px',
  border: !isOpen ? `1px solid rgba(0, 0, 0, 0.12)` : '1px solid rgba(255, 255, 255, 0.12)',
  '&:hover': {
    backgroundColor: isOpen ? myVJBlue200 : 'rgba(0, 0, 0, 0.04)',
    opacity: isOpen ? 0.85 : 1,
  },
  [theme.breakpoints.up("lg")]: {
    display: "none", // Hide on desktop
  }
}));

function Header() {
  const siteState = useAppSiteState();
  const { companyName, sites, selectedSiteId } = siteState;
  const { headerTitleInfo, isLoading } = useAppConfigState();
  const { isAuthenticated, outageStatus } = useAppUserState();
  const { isPureDesignUser } = useAppGlobalState();
  const { selectedCompanyId } = useAppCompanyState();
  const { _T } = useVrsTranslationState();

  const { checkOutageAppSync } = useVrsOutageActions();
  const { addBanner, removeBanner } = useBannerActions();
  const { toggleMobile, mobileOpen } = useMiniSidebar();
  const navigate = useNavigate(); // Add navigate hook

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const smallOrHigher = useMediaQuery(theme.breakpoints.up('md')); // Add this breakpoint check

  const getSelectedSite = () => {
    if (sites && sites.length > 0 && selectedSiteId) {
      return sites.find((el) => el.id === selectedSiteId);
    }
    if (sites && sites.length > 0 && !selectedSiteId) {
      return sites[0];
    }
    return null;
  };

  const selectedSite = getSelectedSite();

  // Add navigation handler
  const handleNavigateHome = () => {
    navigate('/');
  };

  // Handle outage status changes and update banner
  useEffect(() => {
    if (outageStatus) {
      const outageBanner = createOutageBanner(outageStatus, _T);
      if (outageBanner) {
        addBanner(outageBanner);
      }
    } else {
      removeBanner('outage-banner');
    }
  }, [outageStatus, addBanner, removeBanner, _T]);

  // Check for outages periodically
  useEffect(() => {
    const interval = setInterval(() => {
      if (isAuthenticated) {
        checkOutageAppSync();
      }
    }, OUTAGE_CHECK_MILLISECONDS);
    return () => clearInterval(interval);
  }, [checkOutageAppSync, isAuthenticated]);

  const getIconStyle = () => ({
    width: 24,
    height: 24,
    color: mobileOpen ? myVJWhite : myVJBlack950,
    fill: mobileOpen ? myVJWhite : myVJBlack950,
  });

  const showCompanySite = isAuthenticated && selectedSite && (isPureDesignUser || selectedCompanyId && selectedCompanyId !== "0");

  return (
    <HeaderTopContainer>
      <AppBarContainer>
        <StyledToolbar hasHeaderTitleInfo={!!headerTitleInfo && headerTitleInfo.linkParams?.parents}>
          {/* Line 1: Logo + Platform Name + Hamburger (Mobile/Tablet) OR Platform Name + Breadcrumb + Company/Site (Desktop) */}
          <Line1Container>
            <LeftSideContainer>
              {/* Logo - Only show on mobile */}
              {!smallOrHigher && (
                <LogoSection onClick={handleNavigateHome}>
                  <LogoImage src={logo} alt="Spinnaker Logo" />
                </LogoSection>
              )}

              {/* Platform Name */}
              <PlatformName>VideojetConnect™</PlatformName>

              {/* Desktop: Breadcrumb right after platform name */}
              {isDesktop && headerTitleInfo && (
                <BreadcrumbContainer style={{ marginLeft: '96px', marginRight: '16px' }}>
                  <HeaderTitle
                    {...headerTitleInfo}
                    breadcrumbVisible={true}
                    titleVisible={false}
                  />
                </BreadcrumbContainer>
              )}
            </LeftSideContainer>

            {/* Desktop: Company/Site on the right */}
            {isDesktop && showCompanySite && (
              <CompanySiteHeader
                isPureDesignUser={isPureDesignUser}
                companyName={companyName}
                siteName={selectedSite.title}
              />

            )}

            {/* Mobile/Tablet: Hamburger button */}
            {isAuthenticated && !isDesktop && (
              <HamburgerButton
                aria-label={mobileOpen ? "Close navigation menu" : "Open navigation menu"}
                onClick={toggleMobile}
                data-testid="hamburger-menu-btn"
                isOpen={mobileOpen}
              >
                {mobileOpen ? (
                  <HamburgerCloseIcon style={getIconStyle()} />
                ) : (
                  <HamburgerIcon style={getIconStyle()} />
                )}
              </HamburgerButton>
            )}
          </Line1Container>

          {/* Line 2: Company/Site (Mobile/Tablet only) */}
          {!isDesktop && showCompanySite && (
            <Line2Container>
              <CompanySiteHeader
                isPureDesignUser={isPureDesignUser}
                companyName={companyName}
                siteName={selectedSite.title}
              />
            </Line2Container>
          )}

          {/* Line 3: Breadcrumb (Mobile/Tablet only) */}
          {!isDesktop && headerTitleInfo && (
            <Line3Container>
              <BreadcrumbContainer>
                <HeaderTitle
                  {...headerTitleInfo}
                  breadcrumbVisible={true}
                  titleVisible={false}
                />
              </BreadcrumbContainer>
            </Line3Container>
          )}

          {/* Loading indicator */}
          {isLoading && <SimpleLinearProgress />}
        </StyledToolbar>
      </AppBarContainer>

      <BannerContainer />
    </HeaderTopContainer>
  );
}

Header.displayName = 'Header';

export default Header;
